import React, { useContext, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useOrderSetting from "../../../services/Order/useOrderSetting";
import LoadingScreen from "../../../utils/LoadingScreen";
import PrintingSettings from "./PrinterSetting";
import TimingSettings from "./TimingSettings";
import OrderChargesSettings from "./OrderChargeSettings";
import FreeItemsSettings from "./FreeItemsSettings";
import LoyaltySettings from "./LoyaltySettings";
import CouponSettings from "./CouponSettings";
import DiscountSettings from "./DiscountSettings";
import ServicesSettings from "./ServiceSettings";
import DeliverySettings from "./DeliverySettings";
import { RefreshCcw, Smartphone } from "lucide-react";
import { GlobalContext } from "../../../Context/GlobalContext";

const tabs = [
  "delivery",
  "services",
  "discounts",
  "coupon",
  "loyalty",
  "free items",
  "order charges",
  "timing",
  "printing",
  "other",
];

const formatAmount = (val) => parseFloat(val || 0).toFixed(2);
const formatTime = (time) => {
  if (!time) return "--:--";

  const [hour, minute] = time.split(":");
  let h = parseInt(hour);
  const ampm = h >= 12 ? "PM" : "AM";

  h = h % 12 || 12;

  return `${h}:${minute} ${ampm}`;
};

const OrderSettings = () => {
  const [activeTab, setActiveTab] = useState("delivery");

  const {
    loading,
    getDeliveryCharges,
    updateDeliveryCharges,
    getServices,
    updateService,
    getCoupons,
    createCoupon,
    getLoyaltyPoints,
    updateLoyaltyPoints,
    getRestaurantTimingsV2,
    saveRestaurantTimingsV2,
    getRestaurantTiming,
    updateRestaurantTiming,
    getPrintSetting,
    updatePrintSettings,
    deleteDiscount,
    updateDiscount,
    getMerchantMenuItems,
    getOrderCharges,
    getPackaging,
    deleteCoupon,
    addFreeItem,
    getFreeItems,
    updatePackaging,
    addPrinter,
    getPrinters,
    printerList,
    getPrinterSettingAbhi,
    addPrintSettingAbhi,
    updateMobileWhatsapp,
  } = useOrderSetting();

  const [deliveryData, setDeliveryData] = useState([]);
  const [services, setServices] = useState([]);
  const [couponData, setCouponData] = useState([]);
  const [loyalty, setLoyalty] = useState([]);
  const [timing, setTiming] = useState({});
  const [printSettings, setPrintSettings] = useState([]);
  const [catalogueStore, setCatalogueStore] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [orderCharges, setOrderCharges] = useState([]);
  const [packaging, setPackaging] = useState({});
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printForm, setPrintForm] = useState({});
  const [printType, setPrintType] = useState("3");
  const { userID } = useContext(GlobalContext);
  const [whatsappMobile, setWhatsappMobile] = useState("");
  const [updatingWhatsappMobile, setUpdatingWhatsappMobile] = useState(false);

  // ---------------- DISCOUNT STATE ----------------
  const [discountData, setDiscountData] = useState({
    discount: "",
    fromAmount: "",
    toAmount: "",
    discountType: "",
    orderType: "",
    maxDiscount: "",
  });

  const [freeItemForm, setFreeItemForm] = useState({
    orderType: "",
    minAmount: "",
    customerType: "first_time",
    selectedItem: null,
  });

  const [freeItems, setFreeItems] = useState([]);
  const [freeItemsLoading, setFreeItemsLoading] = useState(false);

  const [newCouponData, setNewCouponData] = useState({
    couponCode: "",
    discount: "",
    discountType: "",
    minOrderAmount: "",
    maxDiscountAmount: "",
    couponTitle: "",
    couponDiscription: "",
  });

  const [loyaltyForm, setLoyaltyForm] = useState({
    points: "",
    value: "",
  });

  const [packagingState, setPackagingState] = useState({
    orderWise: false,
    itemWise: false,
    orderAmount: "",
    itemAmount: "",
  });

  const [printerForm, setPrinterForm] = useState({
    printerName: "",
    printerAlias: "",
    deviceName: "",
    deviceNumber: "",
  });

  const handlePrinterChange = (e) => {
    const { name, value } = e.target;
    setPrinterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const normalizeFreeItemsResponse = (response) => {
    const possibleArrays = [
      response?.response?.freeItems,
      response?.response?.free_items,
      response?.response?.items,
      response?.response?.data,
      response?.response,
      response?.data?.freeItems,
      response?.data?.free_items,
      response?.data?.items,
      response?.data,
      response?.result?.freeItems,
      response?.result?.items,
      response?.result,
    ];

    const itemArray = possibleArrays.find((value) => Array.isArray(value));

    if (itemArray) {
      return itemArray.filter(
        (item) =>
          item !== null &&
          item !== undefined &&
          typeof item === "object" &&
          !Array.isArray(item),
      );
    }

    const singleItem = possibleArrays.find(
      (value) =>
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        (value.item_id ||
          value.itemId ||
          value.item_name ||
          value.itemName ||
          value.id),
    );

    return singleItem ? [singleItem] : [];
  };

  const handleFetchFreeItems = async (customValues = null) => {
    const orderType =
      customValues?.orderType !== undefined
        ? customValues.orderType
        : freeItemForm.orderType;

    const amount =
      customValues?.amount !== undefined
        ? customValues.amount
        : freeItemForm.minAmount;

    if (!orderType) {
      toast.error("Please select order type");
      return [];
    }

    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return [];
    }

    setFreeItemsLoading(true);

    try {
      const response = await getFreeItems({
        orderType,
        amount: String(amount),
      });

      if (!response) {
        setFreeItems([]);
        return [];
      }

      const items = normalizeFreeItemsResponse(response);

      setFreeItems(items);

      if (items.length === 0) {
        toast.info(
          response?.responseMessage ||
            "No free items available for this order type and amount",
        );
      }

      return items;
    } catch (error) {
      console.error("FETCH FREE ITEMS ERROR:", error);
      setFreeItems([]);
      toast.error("Failed to fetch free items");
      return [];
    } finally {
      setFreeItemsLoading(false);
    }
  };

  // ---------------- HANDLE CHANGE ----------------
  const handleDiscountChange = (e) => {
    const { name, value } = e.target;
    setDiscountData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRefreshPrintSettings = async () => {
    const updated = await getPrintSetting();

    if (updated) {
      const data = updated.response?.[0]?.printConfigurations || [];
      setPrintSettings(data);

      // ALSO update form immediately
      const obj = Object.fromEntries(
        data.map((item) => [item.key, item.value]),
      );
      setPrintForm(obj);
    }
  };

  // ---------------- UPDATE API ----------------
  const handleUpdateDiscount = async () => {
    const {
      discount,
      fromAmount,
      toAmount,
      discountType,
      orderType,
      maxDiscount,
    } = discountData;

    if (!discount || !fromAmount || !toAmount || !discountType || !orderType) {
      toast.error("Please fill all required fields");
      return;
    }

    const res = await updateDiscount({
      discount,
      fromAmount,
      toAmount,
      discountType,
      orderType,
      maxDiscount,
    });

    if (res?.responseCode === "00") {
      setDiscountData({
        discount: "",
        fromAmount: "",
        toAmount: "",
        discountType: "",
        orderType: "",
        maxDiscount: "",
      });

      const updated = await getDeliveryCharges();

      if (updated) {
        setDeliveryData(updated.data);
      }
    }
  };

  const handleRemoveDiscount = async (discount) => {
    const discountId =
      discount?.discountId ||
      discount?.discount_id ||
      discount?.id ||
      discount?.pk_discount_id ||
      discount?.pk_id;

    if (!discountId) {
      toast.error("Discount ID missing");
      console.log("Discount object:", discount);
      return;
    }

    const res = await deleteDiscount(discountId);

    if (res?.responseCode === "00") {
      const updated = await getDeliveryCharges();

      if (updated) {
        setDeliveryData(updated.data);
      }
    }
  };

  const handleNewCouponChange = (e) => {
    const { name, value } = e.target;
    setNewCouponData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateNewCoupon = async () => {
    const {
      couponCode,
      discount,
      discountType,
      minOrderAmount,
      maxDiscountAmount,
      couponTitle,
      couponDiscription,
    } = newCouponData;

    // validation
    if (
      !couponCode ||
      !discount ||
      !discountType ||
      !minOrderAmount ||
      !maxDiscountAmount ||
      !couponTitle ||
      !couponDiscription
    ) {
      toast.error("Please fill all fields correctly");
      return;
    }

    const payload = {
      couponCode,
      discount,
      discountType,
      minOrderAmount,
      maxDiscountAmount,
      couponTitle,
      couponDiscription,
    };

    const res = await createCoupon(payload);

    if (res) {
      // reset form
      setNewCouponData({
        couponCode: "",
        discount: "",
        discountType: "",
        minOrderAmount: "",
        maxDiscountAmount: "",
        couponTitle: "",
        couponDiscription: "",
      });

      // refresh list
      const updated = await getCoupons();
      if (updated) setCouponData(updated.data);
    }
  };

  // ---------------- LOYALTY FORM ----------------

  const handleLoyaltyChange = (e) => {
    const { name, value } = e.target;
    setLoyaltyForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateLoyalty = async () => {
    const { points, value } = loyaltyForm;

    if (!points || !value) {
      toast.error("Please enter valid values");
      return;
    }

    const res = await updateLoyaltyPoints({
      points,
      value,
    });

    if (res) {
      setLoyaltyForm({ points: "", value: "" });

      const updated = await getLoyaltyPoints();
      if (updated) setLoyalty(updated.data);
    }
  };

  const handleFreeItemChange = (e) => {
    const { name, value } = e.target;
    setFreeItemForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSavePrintType = async () => {
    const res = await addPrintSettingAbhi({
      settings: printType,
    });

    if (res) {
      const updated = await getPrinterSettingAbhi();

      if (updated) {
        const value = updated.response?.[0]?.setting_type || "1";
        setPrintType(String(value));
      }
    }
  };

  const handleToggle = (type) => {
    if (type === "orderWise") {
      setPackagingState((prev) => ({
        ...prev,
        orderWise: !prev.orderWise,
        itemWise: false,
      }));
    }

    if (type === "itemWise") {
      setPackagingState((prev) => ({
        ...prev,
        itemWise: !prev.itemWise,
        orderWise: false,
      }));
    }
  };

  const handlePrintChange = (key, value) => {
    setPrintForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUpdatePackaging = async (customPackagingState = null) => {
    const currentPackagingState = customPackagingState || packagingState;

    const payload = {
      packagingOrderTrigger: currentPackagingState.orderWise ? "Y" : "N",

      packagingOrderCharges: currentPackagingState.orderWise
        ? String(currentPackagingState.orderAmount || "0")
        : "0",

      itemTrigger: currentPackagingState.itemWise ? "Y" : "N",

      itemCharges: currentPackagingState.itemWise
        ? String(currentPackagingState.itemAmount || "0")
        : "0",
    };

    try {
      const res = await updatePackaging(payload);

      if (res) {
        const nextState = {
          orderWise: payload.packagingOrderTrigger === "Y",
          itemWise: payload.itemTrigger === "Y",
          orderAmount: payload.packagingOrderCharges,
          itemAmount: payload.itemCharges,
        };

        setPackagingState(nextState);

        // Keep local packaging object also in sync immediately
        setPackaging({
          packagingOrderWise: payload.packagingOrderTrigger,
          packagingItemWise: payload.itemTrigger,
          packagingOrderWiseCharges: payload.packagingOrderCharges,
          packagingItemCharges: payload.itemCharges,
        });

        const updated = await getPackaging();

        const latestPackaging =
          updated?.response || updated?.data || updated?.result || updated;

        if (latestPackaging) {
          setPackaging(latestPackaging);
        }
      }

      return res;
    } catch (error) {
      console.error(" PACKAGING UPDATE ERROR:", error);
      throw error;
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    const res = await deleteCoupon(couponId, "DELETE");

    if (res?.responseCode === "00" || res) {
      // instant UI update
      setCouponData((prev) =>
        prev.filter((item) => {
          const itemId =
            item?.id || item?.couponId || item?.coupon_id || item?.pk_coupon_id;

          return String(itemId) !== String(couponId);
        }),
      );

      // fresh API sync
      const updated = await getCoupons();

      const latestCoupons =
        updated?.data || updated?.response || updated?.result || [];

      setCouponData(Array.isArray(latestCoupons) ? latestCoupons : []);
    }
  };

  const handleSubmitFreeItem = async () => {
    const { orderType, minAmount, customerType, selectedItem } = freeItemForm;

    if (!orderType) {
      toast.error("Please select order type");
      return;
    }

    if (!minAmount || Number(minAmount) <= 0) {
      toast.error("Please enter a valid minimum order amount");
      return;
    }

    if (!selectedItem) {
      toast.error("Please select a free item");
      return;
    }

    const payload = {
      orderType: String(orderType),
      minAmount: String(minAmount),
      singleUse: customerType === "first_time" ? "Y" : "N",
      item: selectedItem,
    };

    const response = await addFreeItem(payload);

    if (response?.responseCode === "00" || response) {
      await handleFetchFreeItems({
        orderType,
        amount: minAmount,
      });

      setFreeItemForm({
        orderType,
        minAmount: "",
        customerType: "first_time",
        selectedItem: null,
      });
    }
  };

  const handleSavePrinter = async () => {
    const { printerName, printerAlias, deviceName, deviceNumber } = printerForm;

    if (!printerName || !printerAlias || !deviceName || !deviceNumber) {
      toast.error("Please fill all fields");
      return;
    }

    const res = await addPrinter(printerForm);

    if (res) {
      toast.success("Printer added successfully ");

      const updated = await getPrinters();
      if (updated) setPrinters(updated.data || updated.response);

      // reset form
      setPrinterForm({
        printerName: "",
        printerAlias: "",
        deviceName: "",
        deviceNumber: "",
      });

      // close modal
      setShowPrinterModal(false);
    }
  };

  const handleUpdateWhatsappMobile = async (e) => {
    e.preventDefault();

    const cleanedMobile = whatsappMobile.replace(/\D/g, "");

    if (!cleanedMobile) {
      toast.error("Please enter WhatsApp mobile number");
      return;
    }

    if (!cleanedMobile.startsWith("91")) {
      toast.error("Mobile number must start with country code 91");
      return;
    }

    if (cleanedMobile.length !== 12) {
      toast.error("Please enter 91 followed by a valid 10-digit mobile number");
      return;
    }

    const actualMobileNumber = cleanedMobile.slice(2);

    if (!/^[6-9]\d{9}$/.test(actualMobileNumber)) {
      toast.error("Please enter a valid Indian mobile number");
      return;
    }

    setUpdatingWhatsappMobile(true);

    try {
      const res = await updateMobileWhatsapp({
        phone_no: cleanedMobile,
      });

      if (res?.responseCode === "00") {
        setWhatsappMobile("");
      }
    } catch (error) {
      console.error("UPDATE WHATSAPP MOBILE ERROR:", error);
      toast.error("Failed to update WhatsApp mobile number");
    } finally {
      setUpdatingWhatsappMobile(false);
    }
  };

  const handleRefresh = async () => {
    await fetchTabData(activeTab);
  };

  const handleSavePrintSettings = async () => {
    const payload = {
      printsetting: Object.keys(printForm).map((key) => ({
        key,
        value: String(printForm[key]),
      })),
    };

    const res = await updatePrintSettings(payload);

    if (res) {
      //  refresh latest data
      const updated = await getPrintSetting();

      if (updated) {
        setPrintSettings(updated.response?.[0]?.printConfigurations || []);
      }
    }
  };

  const [tabLoading, setTabLoading] = useState(false);

  const fetchTabData = async (tab) => {
    setTabLoading(true);

    try {
      switch (tab) {
        case "delivery": {
          const d = await getDeliveryCharges();
          if (d) setDeliveryData(d.data);
          break;
        }

        case "services": {
          const s = await getServices();
          if (s) setServices(s.response);
          break;
        }

        case "discounts": {
          const d = await getDeliveryCharges();

          if (d) {
            setDeliveryData(d.data);
          }

          break;
        }
        case "coupon": {
          const c = await getCoupons();
          if (c) setCouponData(c.data);
          break;
        }

        case "loyalty": {
          const l = await getLoyaltyPoints();
          if (l) setLoyalty(l.data);
          break;
        }

        // case "timing": {
        //   const t = await getTiming();
        //   if (t) setTiming(t.data);
        //   break;
        // }

        case "timing": {
          break;
        }

        case "printing": {
          const p = await getPrintSetting();
          if (p) {
            const data = p.response?.[0]?.printConfigurations || [];
            setPrintSettings(data);

            const obj = Object.fromEntries(
              data.map((item) => [item.key, item.value]),
            );
            setPrintForm(obj);
          }

          const printerRes = await getPrinters();
          if (printerRes) setPrinters(printerRes.response || []);

          const typeRes = await getPrinterSettingAbhi();
          if (typeRes) {
            const value = typeRes.response?.[0]?.setting_type || "1";
            setPrintType(String(value));
          }

          break;
        }

        case "order charges": {
          const oc = await getOrderCharges();
          if (oc) {
            setOrderCharges(
              oc?.data?.charges || oc?.data || oc?.response || [],
            );
          }

          const pk = await getPackaging();

          if (pk) {
            const packagingData = pk?.response || pk?.data || pk?.result || pk;

            setPackaging(packagingData);
          }
          break;
        }

        case "free items": {
          const [menuResponse, servicesResponse] = await Promise.all([
            getMerchantMenuItems(),
            getServices(),
          ]);

          const menuCategories =
            menuResponse?.response ||
            menuResponse?.data ||
            menuResponse?.result ||
            [];

          if (Array.isArray(menuCategories)) {
            const uniqueMenu = menuCategories
              .filter(
                (category) =>
                  category !== null &&
                  category !== undefined &&
                  typeof category === "object" &&
                  !Array.isArray(category),
              )
              .map((category) => {
                const menuItems = Array.isArray(category?.menu_items)
                  ? category.menu_items.filter(
                      (item) =>
                        item !== null &&
                        item !== undefined &&
                        typeof item === "object" &&
                        !Array.isArray(item),
                    )
                  : [];

                const uniqueItemsMap = new Map();

                menuItems.forEach((item, index) => {
                  const itemId =
                    item?.item_id ||
                    item?.itemId ||
                    item?.id ||
                    item?.pk_item_id ||
                    `item-${index}`;

                  if (!uniqueItemsMap.has(String(itemId))) {
                    uniqueItemsMap.set(String(itemId), item);
                  }
                });

                return {
                  ...category,
                  menu_items: Array.from(uniqueItemsMap.values()),
                };
              });

            setCatalogueStore(uniqueMenu);
          } else {
            setCatalogueStore([]);
          }

          const orderTypeServices =
            servicesResponse?.response ||
            servicesResponse?.data ||
            servicesResponse?.result ||
            [];

          setServices(
            Array.isArray(orderTypeServices)
              ? orderTypeServices.filter(
                  (service) =>
                    service !== null &&
                    service !== undefined &&
                    typeof service === "object" &&
                    !Array.isArray(service),
                )
              : [],
          );

          break;
        }

        case "other": {
          break;
        }

        default:
          break;
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load data");
    } finally {
      setTabLoading(false);
    }
  };

  // ---------------- FETCH ----------------
  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!packaging || (Array.isArray(packaging) && packaging.length === 0)) {
      return;
    }

    const data = Array.isArray(packaging) ? packaging[0] : packaging;

    const orderChargeAmount = Number(data?.packagingOrderWiseCharges || 0);
    const itemChargeAmount = Number(data?.packagingItemCharges || 0);

    const nextPackagingState = {
      orderWise: data?.packagingOrderWise === "Y" && orderChargeAmount > 0,
      itemWise: data?.packagingItemWise === "Y" && itemChargeAmount > 0,

      orderAmount: String(orderChargeAmount),
      itemAmount: String(itemChargeAmount),
    };

    setPackagingState(nextPackagingState);
  }, [packaging]);

  useEffect(() => {
    if (printSettings.length > 0) {
      const obj = Object.fromEntries(
        printSettings.map((item) => [item.key, item.value]),
      );
      setPrintForm(obj);
    }
  }, [printSettings]);

  const printConfig = Object.fromEntries(
    (printSettings || []).map((item) => [item.key, item.value]),
  );

  return (
    <>
      <div className="p-6 h-full flex flex-col overflow-hidden montserrat-font-1">
        <div className="sticky top-0 z-30 pb-4 flex-shrink-0">
          <div className="flex justify-between items-center mb-6 montserrat-font-1">
            <h1 className="text-2xl font-bold">Order Settings</h1>

            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6 montserrat-font-1">
            <nav className="flex gap-6">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-2 text-sm font-medium capitalize border-b-2 transition-all ${
                    activeTab === tab
                      ? "border-green-600 text-green-600"
                      : "border-transparent text-gray-500 hover:text-green-600"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 pt-4">
          {tabLoading && (
            <div className="flex justify-center items-center py-6">
              <LoadingScreen text="Loading tab data..." />
            </div>
          )}

          {!tabLoading && (
            <>
              {/* ---------------- DELIVERY ---------------- */}

              {activeTab === "delivery" && (
                <DeliverySettings deliveryData={deliveryData} />
              )}

              {/* ---------------- SERVICES ---------------- */}

              {activeTab === "services" && (
                <ServicesSettings
                  services={services}
                  updateService={updateService}
                  setServices={setServices}
                />
              )}

              {/* ---------------- Discount ---------------- */}

              {activeTab === "discounts" && (
                <DiscountSettings
                  discountData={discountData}
                  handleDiscountChange={handleDiscountChange}
                  handleUpdateDiscount={handleUpdateDiscount}
                  handleRemoveDiscount={handleRemoveDiscount}
                  loading={loading}
                  discounts={deliveryData?.discount || []}
                  formatAmount={formatAmount}
                />
              )}
              {/* ---------------- COUPON ---------------- */}

              {activeTab === "coupon" && (
                <CouponSettings
                  couponData={couponData}
                  formatAmount={formatAmount}
                  deleteCoupon={deleteCoupon}
                  handleDeleteCoupon={handleDeleteCoupon}
                  setCouponData={setCouponData}
                  newCouponData={newCouponData}
                  handleNewCouponChange={handleNewCouponChange}
                  handleCreateNewCoupon={handleCreateNewCoupon}
                />
              )}

              {/* ---------------- LOYALTY ---------------- */}

              {activeTab === "loyalty" && (
                <LoyaltySettings
                  loyalty={loyalty}
                  loyaltyForm={loyaltyForm}
                  handleLoyaltyChange={handleLoyaltyChange}
                  handleUpdateLoyalty={handleUpdateLoyalty}
                  formatAmount={formatAmount}
                />
              )}

              {/* ---------------- Free Items ---------------- */}

              {activeTab === "free items" && (
                <FreeItemsSettings
                  freeItemForm={freeItemForm}
                  handleFreeItemChange={handleFreeItemChange}
                  handleSubmitFreeItem={handleSubmitFreeItem}
                  handleFetchFreeItems={handleFetchFreeItems}
                  catalogueStore={catalogueStore}
                  expandedCategory={expandedCategory}
                  setExpandedCategory={setExpandedCategory}
                  setFreeItemForm={setFreeItemForm}
                  freeItems={freeItems}
                  freeItemsLoading={freeItemsLoading}
                  services={services}
                  loading={loading}
                />
              )}

              {/* ---------------- Order Charges ---------------- */}

              {activeTab === "order charges" && (
                <OrderChargesSettings
                  orderCharges={orderCharges}
                  packagingState={packagingState}
                  setPackagingState={setPackagingState}
                  handleToggle={handleToggle}
                  handleUpdatePackaging={handleUpdatePackaging}
                />
              )}

              {/* ---------------- TIMING ---------------- */}

              {activeTab === "timing" && (
                <TimingSettings
                  userID={userID}
                  getRestaurantTiming={getRestaurantTiming}
                  updateRestaurantTiming={updateRestaurantTiming}
                  getRestaurantTimingsV2={getRestaurantTimingsV2}
                  saveRestaurantTimingsV2={saveRestaurantTimingsV2}
                />
              )}

              {/* ---------------- PRINT ---------------- */}

              {activeTab === "printing" && (
                <PrintingSettings
                  printSettings={printSettings}
                  printConfig={printConfig}
                  printForm={printForm}
                  handlePrintChange={handlePrintChange}
                  handleSavePrintSettings={handleSavePrintSettings}
                  printType={printType}
                  setPrintType={setPrintType}
                  handleSavePrintType={handleSavePrintType}
                  printers={printers}
                  setShowPrinterModal={setShowPrinterModal}
                  showPrinterModal={showPrinterModal}
                  printerForm={printerForm}
                  handlePrinterChange={handlePrinterChange}
                  handleSavePrinter={handleSavePrinter}
                  handleRefreshPrintSettings={handleRefreshPrintSettings}
                  getPrinters={getPrinters}
                />
              )}

              {/* ---------------- OTHER ---------------- */}

              {activeTab === "other" && (
                <div className="max-w-2xl">
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-200">
                      <div className="flex items-center justify-center w-11 h-11 bg-green-100 rounded-full">
                        <Smartphone className="w-5 h-5 text-green-600" />
                      </div>

                      <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                          WhatsApp Mobile Number
                        </h2>

                        <p className="text-sm text-gray-500 mt-1">
                          Update the WhatsApp number used for order
                          notifications.
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleUpdateWhatsappMobile} className="p-6">
                      <div className="mb-5">
                        <label
                          htmlFor="whatsappMobile"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Mobile Number
                        </label>

                        <input
                          id="whatsappMobile"
                          type="tel"
                          value={whatsappMobile}
                          onChange={(e) =>
                            setWhatsappMobile(e.target.value.replace(/\D/g, ""))
                          }
                          placeholder="Enter number with 91, e.g. 919910254756"
                          maxLength={12}
                          autoComplete="tel"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
                        />

                        <p className="text-xs text-gray-500 mt-2">
                          Example: 919910254756
                        </p>
                      </div>

                      <button
                        type="submit"
                        disabled={updatingWhatsappMobile || loading}
                        className="flex items-center justify-center gap-2 px-5 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition"
                      >
                        <Smartphone className="w-4 h-4" />

                        {updatingWhatsappMobile || loading
                          ? "Updating..."
                          : "Update WhatsApp Number"}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderSettings;
